import { formatCurrency } from '@/src/utils/currency/formatCurrency'

export { setHotelUpstreamHeaders } from './hotelUpstreamHeaders'

const THREE_DECIMAL_CURRENCIES = ['BHD', 'OMR', 'KWD']
const TWO_DECIMAL_CURRENCIES = ['USD', 'EUR', 'GBP']

/** GCC display convention: whole amounts for most currencies, 3dp for BHD/OMR/KWD. */
const getHotelCurrencyPrecision = (currency: string): number => {
    if (THREE_DECIMAL_CURRENCIES.includes(currency)) return 3
    if (TWO_DECIMAL_CURRENCIES.includes(currency)) return 2
    return 0
}

export const formatHotelCurrency = (amount: number, currency: string, locale: string): string => {
    if (!currency) return `${amount}`
    return formatCurrency({
        amount,
        currency,
        locale: locale === 'ar' ? 'ar' : 'en',
        precision: getHotelCurrencyPrecision(currency)
    })
}

/** "2026/07/01" (y/m/d, from bookOtherRoom cin/cout) → "1 Jul 2026" localized. */
export const formatHotelDate = (raw: string, locale: string): string => {
    // Upstream sends year-first, but the separator varies by endpoint (`2026-08-10` from
    // selectHotel, `2026/08/10` elsewhere) — accept either rather than echoing the raw value.
    const [y, m, d] = raw.split(/[/-]/)
    const date = new Date(Number(y), Number(m) - 1, Number(d))
    if (isNaN(date.getTime())) return raw
    return `${date.getDate()} ${date.toLocaleString(locale, { month: 'short' })} ${date.getFullYear()}`
}

/**
 * Joins display segments, dropping any that repeats the one before it. Cleartrip files
 * every emirate as its own state, so a raw join reads "Dubai, Dubai, United Arab
 * Emirates" — this collapses it to "Dubai, United Arab Emirates".
 */
export const joinSegments = (segments: Array<string | undefined | null>): string => {
    const parts: string[] = []
    segments.forEach(segment => {
        const value = segment?.trim()
        if (value && value !== parts[parts.length - 1]) parts.push(value)
    })
    return parts.join(', ')
}

const CT_MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec']

/**
 * Cleartrip display date "Wed,26,Aug,2026" → "2026-08-26", ready for `formatHotelDate`.
 *
 * Preferred over Cleartrip's epoch-millis fields: those are midnight in the property's
 * timezone, so reading them through a browser `Date` can land on the previous day.
 */
export const ctDisplayDateToIso = (display?: string | null): string => {
    if (!display) return ''
    const parts = display.split(',').map(part => part.trim())
    if (parts.length < 4) return ''
    const [, day, month, year] = parts
    const monthIndex = CT_MONTHS.indexOf(month.slice(0, 3).toLowerCase())
    if (monthIndex < 0 || !day || !year) return ''
    return `${year}-${String(monthIndex + 1).padStart(2, '0')}-${day.padStart(2, '0')}`
}

/** Sum a per-room CSV like "2,2" → 4. */
export const sumCsv = (csv: string): number => csv.split(',').reduce((acc, n) => acc + (Number(n) || 0), 0)

/** "2026-08-19" (ISO, from the SRP query string) → "19 Aug" localized. */
export const formatHotelIsoDate = (iso: string, locale: string): string => {
    const [y, m, d] = iso.split('-')
    const date = new Date(Number(y), Number(m) - 1, Number(d))
    if (isNaN(date.getTime())) return iso
    return `${date.getDate()} ${date.toLocaleString(locale, { month: 'short' })}`
}

